import { Component, EventEmitter, Output, inject, input, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@app/core/validators/custom-validators';
import { Facebook, FacebookLoginResponse } from '@awesome-cordova-plugins/facebook/ngx';

// models
import { FacebookLoginProvider, SocialAuthService } from '@abacritt/angularx-social-login';
import { I18nService } from '@app/core/i18n/i18n.service';
import { RouteRedirectAfterAction } from '@app/core/route-redirect-after-action';
import { ToolsService } from '@app/core/services';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { DeviceDetectService } from '@app/core/services/device-detect/device-detect.service';
import { CustomService } from '@app/core/services/drupal8';
import { GoogleLoginService } from '@app/core/services/google/google-login.service';
import { LoggerService } from '@app/core/services/logger/logger.service';
import { ConfirmModalResult, ModalManagerService, ModalTypes } from '@app/core/services/modal-manager';
import { NotificationsService } from '@app/core/services/notifications/notifications.service';
import { RolesService } from '@app/core/services/roles/roles.service';
import { SplashScreenService } from '@app/core/services/splash-screen/splash-screen.service';
import { SubscriptionPlansService } from '@app/core/services/subscription-plans/subscription-plans.service';
import { SpamValidators } from '@app/core/validators/spam-validators';
import { LoaderWithWordsService } from '@app/shared';
import { TranslateService } from '@ngx-translate/core';
declare const navigator: any;

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent extends RouteRedirectAfterAction {
  registerForm?: FormGroup;
  displayFacebook = true;

  displayApple = this.deviceDetectService.device.platform === 'iOS';
  loading = false;
  errorMessage?: string;
  /**
   * Input to check if the login is in a modal.
   */
  readonly isInModal = input<boolean>(false);
  /**
   * Event emitter to request the register page.
   */
  @Output() requestLogin: EventEmitter<void> = new EventEmitter<void>(false);
  /**
   * Event emitter to notify the login is done.
   */
  @Output() registerDone: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Event emitter to notify the login is in progress.
   */
  @Output() isLogging: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  protected readonly displayGoogle = signal(true);
  protected afterDestinationRoute = signal<boolean | null>(null);
  protected toolsService: ToolsService = inject(ToolsService);
  private notificationsService: NotificationsService = inject(NotificationsService);
  private authSessionService: AuthSessionService = inject(AuthSessionService);
  private loggerService: LoggerService = inject(LoggerService);
  private readonly googleLoginService: GoogleLoginService = inject(GoogleLoginService);

  constructor(
    public deviceDetectService: DeviceDetectService,
    private formBuilder: FormBuilder,
    private facebook: Facebook,
    private socialAuthService: SocialAuthService,
    private translateService: TranslateService,
    private rolesService: RolesService,
    private customService: CustomService,
    private subscriptionPlansService: SubscriptionPlansService,
    private i18nService: I18nService,
    private loaderWords: LoaderWithWordsService,
    private splashScreenService: SplashScreenService,
    private modalManagerService: ModalManagerService
  ) {
    super();
    this.displayApple = this.deviceDetectService.device.platform === 'iOS';
    this.buildForm();
  }

  /**
   * building the register form
   */
  buildForm() {
    // build the form
    this.registerForm = this.formBuilder.group({
      mail: ['', [Validators.required, CustomValidators.email]],
      first_name: [
        '',
        [
          Validators.required,
          SpamValidators.maxWords(4),
          SpamValidators.checkBlackListWords,
          SpamValidators.spekableCharacters,
          SpamValidators.checkLinks,
        ],
      ],
      last_name: [
        '',
        [
          Validators.required,
          SpamValidators.maxWords(4),
          SpamValidators.checkBlackListWords,
          SpamValidators.spekableCharacters,
          SpamValidators.checkLinks,
        ],
      ],
    });

    // set name from email
    this.registerForm.controls.mail.valueChanges.subscribe((value) => {
      this.displayApple = !value && this.deviceDetectService.device.platform === 'iOS';
      this.displayFacebook = !value;
      this.displayGoogle.set(!value);
    });
  }

  /**
   * Handle the registration with the form.
   */
  register(force = false) {
    // if the form is not valid just do nothing
    if (!this.registerForm?.valid) {
      this.registerForm?.markAllAsTouched();
      this.registerForm?.updateValueAndValidity();
      return;
    }
    const userToCreate: any = {
      mail: this.registerForm?.value.mail,
      full_name: this.registerForm?.value.first_name + ' ' + this.registerForm?.value.last_name,
      preferred_langcode: this.i18nService.language.toLowerCase(),
    };

    if (force) {
      userToCreate.bypass_validate_full_name = true;
    }
    this.loaderWords.show();
    // create the user
    this.customService.registerUser(userToCreate).subscribe({
      next: (response) => {
        if (response.status) {
          this.navigateToProfile();
          return;
        }
        /* eslint-disable @typescript-eslint/dot-notation */
        let errorMessage;
        console.log(response);

        switch (true) {
          case !!(response.message as any)['mail']:
          case !!(response.message as any)['mail_invalid']:
            errorMessage = this.translateService.instant('Email does not seems to be real, please check it');
            break;
          case !!(response.message as any)['mail_exists']:
            errorMessage = this.translateService.instant('Email address already used');
            break;
          case !!(response.message as any)['full_name_exists']:
            this.handleFullNameExistsError((response.message as any)['full_name_exists']);
            return;
          default:
            errorMessage = this.translateService.instant('An error occurred creating the new account');
        }

        this.notificationsService.notifyError(errorMessage);

        this.loaderWords.hide();
      },
      error: () => {
        this.loaderWords.hide();
        this.notificationsService.notifyError(
          this.translateService.instant('An error occurred creating the new account')
        );
      },
    });
  }

  /**
   * Handle the login with apple.
   */
  loginApple() {
    this.loaderWords.show();
    this.customService.appleLogin().subscribe({
      next: () => {
        this.navigateToProfile();
      },
      error: (err) => {
        const genericMessage = this.translateService.instant('An error occured while login with Apple');
        this.notificationsService.notifyError(
          err?.status === 0 ? genericMessage : err?.error?.message ?? err.message ?? genericMessage
        );
        this.loaderWords.hide();
      },
    });
  }

  /**
   * handle the register with facebook function
   */
  registerFacebook() {
    this.loaderWords.show();
    if (!this.deviceDetectService.isCordova) {
      this.socialAuthService
        .signIn(FacebookLoginProvider.PROVIDER_ID)
        .then((response) => {
          this.loginWithFacebookToken(response.authToken);
        })
        .catch((err) => this.handleFacebookLoginError(err));
      return;
    }
    this.facebook.login(['public_profile', 'email']).then(
      (res: FacebookLoginResponse) => {
        // request drupal backend
        if (res.status !== 'connected') {
          this.notificationsService.notifyError(this.translateService.instant('Error on login with facebook'));
          this.loaderWords.hide();
          return;
        }
        this.loginWithFacebookToken(res.authResponse.accessToken);
      },
      (err) => {
        this.handleFacebookLoginError(err);
      }
    );
  }

  /**
   * Handles the Google login process when the associated button is clicked.
   *
   * This method initializes the Google OAuth2 token client and requests an access token
   * with the specified scopes. Upon receiving the token, it attempts to log in the user
   * via the custom service. If successful, the user is navigated to their profile.
   * Otherwise, appropriate error notifications are displayed.
   *
   * @param $event - The event object associated with the click action.
   *
   * @remarks
   * - Prevents the default behavior and propagation of the click event.
   * - Uses the `google.accounts.oauth2.initTokenClient` to handle the OAuth2 flow.
   * - Displays a loader during the login process and hides it in case of an error.
   * - Notifies the user of any errors encountered during the login process.
   *
   * @throws Will log an error to the console if the token response is invalid.
   */
  protected handleClickGoogleLogin($event: Event) {
    this.googleLoginService.executeLogin($event).subscribe({
      next: () => this.navigateToProfile('/profile'),
      error: (err) => {
        this.loaderWords.hide();
        // this.splashScreenService.hide();
        this.notificationsService.notifyError(err?.error?.message ?? err.message ?? 'Error login in the app');
      },
    });
  }

  /**
   * Handles the error scenario where a full name already exists in the system.
   * This method processes the server response to extract relevant information
   * and displays appropriate notifications or modals to the user.
   *
   * @param response - The server response string containing details about the error.
   *
   * The method performs the following steps:
   * - Hides the loader.
   * - Logs an error if the response is empty and notifies the user of a generic error.
   * - Extracts email addresses from the response if available.
   * - Displays a confirmation modal with the extracted email addresses, allowing the user
   *   to either proceed with creating a new account or cancel the operation.
   *
   * If the user confirms the action in the modal, the method triggers the registration process.
   */
  private handleFullNameExistsError(response: string) {
    this.loaderWords.hide();
    if (!response) {
      this.loggerService.logError('Full name exists error byt empty response ' + response);
      return this.notificationsService.notifyError(
        this.translateService.instant('An error occurred creating the new account')
      );
    }
    const firstIndex: number = response.indexOf('(es):');
    const lastIndex: number = response.indexOf('<br>', firstIndex);
    if (lastIndex === -1) {
      this.loggerService.logError('Full name exists error not email found it ' + response);
      return this.notificationsService.notifyError(
        this.translateService.instant('An error occurred creating the new account')
      );
    }
    const emailsString: string = response.substring(firstIndex + 5, lastIndex);
    const emails = emailsString
      .split(', ')
      .map((email) => email.trim())
      .filter((email) => email)
      .map((email) => `<b>${email}</b><br>`)
      .join('');
    this.modalManagerService
      .openConfirmModal(
        this.translateService.instant('Warning'),
        this.translateService.instant('message_registration_name_surname_already_exists', {
          name: this.registerForm?.value.first_name,
          lastName: this.registerForm?.value.last_name,
          emails,
        }),
        {
          buttonYesLabel: this.translateService.instant('Create new one'),
          buttonNoLabel: this.translateService.instant('Cancel'),
        }
      )
      .subscribe((result) => {
        if (result === ConfirmModalResult.yes) {
          this.register(true);
        }
      });
  }

  /**
   * Navigates the user to the profile page or a specified path after performing
   * several initialization tasks. This includes showing a splash screen, hiding
   * loader words, initializing the user profile, setting up roles, resetting
   * navigation data, initializing push notifications, fetching subscription plans,
   * and opening a segmentation form modal. Additionally, it updates the user
   * timezone and handles navigation based on whether the current view is a modal.
   *
   * @param path - The navigation path to redirect to. Defaults to '/profile'.
   */
  private navigateToProfile(path: string = '/profile') {
    this.splashScreenService.show();
    this.loaderWords.hide();
    // init push notification
    // this.customService.trackLocation();
    this.authSessionService.initProfile().subscribe((userProfile) => {
      this.rolesService.init(userProfile);
      this.navDataService.resetHeaderRightButton();
      this.customService.initPush();
      this.navDataService.storage = true;
      this.subscriptionPlansService.get().subscribe();
      this.showPasswordSentMessage();
      // update the user timezone
      // this.customService.updateUserTimeZone();
      this.modalManagerService.open(ModalTypes.segmentationForm).subscribe();
      this.splashScreenService.hide();
      if (this.isInModal()) {
        this.registerDone.emit();
      } else {
        this.executeNavigation(path);
      }
    });
  }

  /**
   * Show welcome message with instruction about the password after registration.
   *
   */
  private showPasswordSentMessage(): void {
    const messageBody = this.translateService.instant('welcome_password_message_body');
    const messageTitle = this.translateService.instant('welcome_password_message_title');
    if (!navigator.notification) {
      alert(messageTitle + '\n' + messageBody);
      return;
    }
    navigator.notification.alert(messageBody, null, messageTitle);
  }

  /**
   * Try to login with the facebook token.
   *
   * @param authToken
   *  Authentication token from facebook.
   */
  private loginWithFacebookToken(authToken: string) {
    this.splashScreenService.show();
    this.customService.facebookConnect(authToken).subscribe({
      next: () => {
        this.navigateToProfile();
      },
      error: (err) => {
        this.loaderWords.hide();
        this.splashScreenService.hide();
        this.notificationsService.notifyError(err?.error?.message ?? err.message ?? 'Error login in the app');
      },
    });
  }

  /**
   * Handle the facebook login error.
   *
   * @param err
   *  Error from facebook login.
   */
  private handleFacebookLoginError(err: any) {
    // 4201 it's the error code for the user cancel the login.
    if (err?.errorCode !== '4201') {
      this.notificationsService.notifyError(this.translateService.instant('Error on login with facebook'));
    }
    this.loaderWords.hide();
    this.loggerService.logError('FB Login: ' + err.message);
  }
}
